import React from 'react';
import { HardDriveIcon, BotIcon, FileTextIcon, UsersIcon, AlertCircleIcon } from 'lucide-react';
interface Props {
  usage: {
    storage: {
      used: number;
      total: number;
      unit: string;
    };
    documents: {
      count: number;
      limit: number;
    };
    chatbots: {
      count: number;
      limit: number;
    };
    users: {
      count: number;
      limit: number;
    };
  };
}
export const ResourceUsageSection = ({
  usage
}: Props) => {
  const getUsagePercentage = (used: number, total: number) => {
    return used / total * 100;
  };
  const getStatusColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-status-error';
    if (percentage >= 75) return 'bg-status-warning';
    return 'bg-accent';
  };
  return <div className="space-y-6 bg-surface-light border border-surface-border p-4 sm:p-6 rounded-none" data-id="element-1292">
      <div data-id="element-1293">
        <h3 className="text-lg font-medium text-white font-mono" data-id="element-1294">
          Resource Usage
        </h3>
        <p className="text-sm text-gray-400 mt-1" data-id="element-1295">
          Monitor your resource consumption
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1296">
        {/* Storage Usage */}
        <div className="p-4 bg-surface border border-surface-border rounded-none" data-id="element-1297">
          <div className="flex items-start gap-3" data-id="element-1298">
            <div className="p-2 bg-surface-mid border border-surface-border rounded-none" data-id="element-1299">
              <HardDriveIcon size={18} className="text-accent-light" data-id="element-1300" />
            </div>
            <div className="flex-1" data-id="element-1301">
              <div className="flex items-center justify-between mb-2" data-id="element-1302">
                <h4 className="text-sm font-medium text-white" data-id="element-1303">Storage</h4>
                <span className="text-xs text-gray-400 font-mono" data-id="element-1304">
                  {usage.storage.used}/{usage.storage.total}{' '}
                  {usage.storage.unit}
                </span>
              </div>
              <div className="h-1.5 bg-surface-border rounded-none overflow-hidden" data-id="element-1305">
                <div className={`h-full ${getStatusColor(getUsagePercentage(usage.storage.used, usage.storage.total))} transition-all duration-300`} style={{
                width: `${getUsagePercentage(usage.storage.used, usage.storage.total)}%`
              }} data-id="element-1306" />
              </div>
            </div>
          </div>
        </div>
        {/* Documents Usage */}
        <div className="p-4 bg-surface border border-surface-border rounded-none" data-id="element-1307">
          <div className="flex items-start gap-3" data-id="element-1308">
            <div className="p-2 bg-surface-mid border border-surface-border rounded-none" data-id="element-1309">
              <FileTextIcon size={18} className="text-accent-light" data-id="element-1310" />
            </div>
            <div className="flex-1" data-id="element-1311">
              <div className="flex items-center justify-between mb-2" data-id="element-1312">
                <h4 className="text-sm font-medium text-white" data-id="element-1313">Documents</h4>
                <span className="text-xs text-gray-400 font-mono" data-id="element-1314">
                  {usage.documents.count}/{usage.documents.limit}
                </span>
              </div>
              <div className="h-1.5 bg-surface-border rounded-none overflow-hidden" data-id="element-1315">
                <div className={`h-full ${getStatusColor(getUsagePercentage(usage.documents.count, usage.documents.limit))} transition-all duration-300`} style={{
                width: `${getUsagePercentage(usage.documents.count, usage.documents.limit)}%`
              }} data-id="element-1316" />
              </div>
            </div>
          </div>
        </div>
        {/* Chatbots Usage */}
        <div className="p-4 bg-surface border border-surface-border rounded-none" data-id="element-1317">
          <div className="flex items-start gap-3" data-id="element-1318">
            <div className="p-2 bg-surface-mid border border-surface-border rounded-none" data-id="element-1319">
              <BotIcon size={18} className="text-accent-light" data-id="element-1320" />
            </div>
            <div className="flex-1" data-id="element-1321">
              <div className="flex items-center justify-between mb-2" data-id="element-1322">
                <h4 className="text-sm font-medium text-white" data-id="element-1323">Chatbots</h4>
                <span className="text-xs text-gray-400 font-mono" data-id="element-1324">
                  {usage.chatbots.count}/{usage.chatbots.limit}
                </span>
              </div>
              <div className="h-1.5 bg-surface-border rounded-none overflow-hidden" data-id="element-1325">
                <div className={`h-full ${getStatusColor(getUsagePercentage(usage.chatbots.count, usage.chatbots.limit))} transition-all duration-300`} style={{
                width: `${getUsagePercentage(usage.chatbots.count, usage.chatbots.limit)}%`
              }} data-id="element-1326" />
              </div>
            </div>
          </div>
        </div>
        {/* Users Usage */}
        <div className="p-4 bg-surface border border-surface-border rounded-none" data-id="element-1327">
          <div className="flex items-start gap-3" data-id="element-1328">
            <div className="p-2 bg-surface-mid border border-surface-border rounded-none" data-id="element-1329">
              <UsersIcon size={18} className="text-accent-light" data-id="element-1330" />
            </div>
            <div className="flex-1" data-id="element-1331">
              <div className="flex items-center justify-between mb-2" data-id="element-1332">
                <h4 className="text-sm font-medium text-white" data-id="element-1333">Users</h4>
                <span className="text-xs text-gray-400 font-mono" data-id="element-1334">
                  {usage.users.count}/{usage.users.limit}
                </span>
              </div>
              <div className="h-1.5 bg-surface-border rounded-none overflow-hidden" data-id="element-1335">
                <div className={`h-full ${getStatusColor(getUsagePercentage(usage.users.count, usage.users.limit))} transition-all duration-300`} style={{
                width: `${getUsagePercentage(usage.users.count, usage.users.limit)}%`
              }} data-id="element-1336" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Warning for high usage */}
      {Object.entries(usage).some(([key, value]: [string, any]) => getUsagePercentage(key === 'storage' ? value.used : value.count, key === 'storage' ? value.total : value.limit) >= 90) && <div className="flex items-start gap-3 p-4 bg-status-error/10 border border-status-error/30 rounded-none" data-id="element-1337">
          <AlertCircleIcon size={20} className="text-status-error shrink-0 mt-0.5" data-id="element-1338" />
          <div data-id="element-1339">
            <p className="text-sm font-medium text-status-error" data-id="element-1340">
              Resource Usage Warning
            </p>
            <p className="text-sm text-status-error/80 mt-1" data-id="element-1341">
              Some of your resources are nearing their limits. Consider
              upgrading your plan or contacting support for assistance.
            </p>
          </div>
        </div>}
    </div>;
};